import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

import { requiredFileCount } from 'utils/applicationHelper';
import Button from 'components/button';
import FormGroup from 'components/form-group';
import ShadowRender from 'components/shadow-render';

import { Row, Title, PageContainer, PageWrapper, Description } from '../common-styles';
import FileUpload from '../upload';

const StyledFormGroup = styled(FormGroup)`
  .group-label {
    font-weight: bold;
    font-size: 0.94em;
  }
`;
const TypeWarning = styled(Description)`
  color: red;
`;

const TeamUploadNotice = styled.div`
  font-size: 0.88em;
  color: #2e7d32;
  background: #f1f8e9;
  border: 1px solid #c5e1a5;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 8px;
`;

const PDF_TYPES = ['application/pdf'];
const OTHER_TYPES = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

const Upload = ({
  label,
  application,
  field,
  requirePDF,
  required,
  localFiles = {},
  setLocalFiles
}) => {
  const existingFile = (application?.files || {})[field];
  return (
    <FileUpload
      required={required}
      autoUpload={true}
      localFiles={localFiles}
      setLocalFiles={setLocalFiles}
      locationId={application.locationId}
      applicationId={application._id}
      field={field}
      existingFile={existingFile}
      legacy={false}
      label={label}
      enforceFileTypes={true}
      fileTypes={requirePDF ? PDF_TYPES : PDF_TYPES.concat(OTHER_TYPES)}
    />
  );
};

const MAX_FILE_COUNT = 7;

export const IncomeFiles = ({
  application,
  location,
  manualVerification,
  onBack,
  onNext,
  onRefresh,
  hideBack
}) => {
  const [localFiles, setLocalFiles] = React.useState(application.files || {});

  const handleNext = () => {
    manualVerification({ type: 'income' }).then((success) => {
      if (!success) return;
      onRefresh();
      onNext();
    });
  };

  const requiredTaxDocCount = requiredFileCount('tax', application, location);
  const requiredBankStatementCount = requiredFileCount('bank', application, location);
  const requiredPayStubCount = requiredFileCount('paystub', application, location);
  const requirePDF = !!location?.companyInfo?.requirePDFUploads;

  const fileRefPayCount = (application.fileRefs || []).filter((r) => r.type === 'pay').length;
  const fileRefBankCount = (application.fileRefs || []).filter((r) => r.type === 'bank').length;
  const fileRefTaxCount = (application.fileRefs || []).filter((r) => r.type === 'tax').length;

  const { stubUploadCount, bankUploadCount, taxUploadCount } = Object.keys(localFiles || {}).reduce(
    (counts, key) => {
      if (key.startsWith('pay') && localFiles[key]) {
        counts.stubUploadCount = counts.stubUploadCount + 1;
      }
      if (key.startsWith('bank') && localFiles[key]) {
        counts.bankUploadCount = counts.bankUploadCount + 1;
      }
      if (key.startsWith('tax') && localFiles[key]) {
        counts.taxUploadCount = counts.taxUploadCount + 1;
      }
      return counts;
    },
    {
      stubUploadCount: fileRefPayCount,
      bankUploadCount: fileRefBankCount,
      taxUploadCount: fileRefTaxCount
    }
  );

  const remainingPayRequired = Math.max(0, requiredPayStubCount - fileRefPayCount);
  const remainingBankRequired = Math.max(0, requiredBankStatementCount - fileRefBankCount);
  const remainingTaxRequired = Math.max(0, requiredTaxDocCount - fileRefTaxCount);

  const requirementsMet =
    (!requiredPayStubCount || stubUploadCount >= requiredPayStubCount) &&
    (!requiredBankStatementCount || bankUploadCount >= requiredBankStatementCount) &&
    (!requiredTaxDocCount || taxUploadCount >= requiredTaxDocCount);

  return (
    <PageWrapper>
      <PageContainer>
        <TypeWarning>
          {t('income_documents.subDescription')}
          {!requirePDF && (
            <>
              <br />
              {t('income_documents.subDescription2')}
            </>
          )}
        </TypeWarning>
        <Row>
          <StyledFormGroup
            label={`${t('income_documents.paystub')} ${
              requiredPayStubCount ? `${stubUploadCount}/${requiredPayStubCount}` : ''
            }`}
            subText={
              requiredPayStubCount
                ? `${stubUploadCount} of ${requiredPayStubCount} required files uploaded`
                : ''
            }>
            <div>
              {fileRefPayCount > 0 && (
                <TeamUploadNotice>
                  {fileRefPayCount} file{fileRefPayCount !== 1 ? 's' : ''} uploaded on your behalf
                  by our team
                </TeamUploadNotice>
              )}
              {Array.from({ length: MAX_FILE_COUNT }).map((unused, ix) => {
                const fileNum = ix + 1;
                const hasPrevFile =
                  !!localFiles?.[`pay${fileNum - 1}`] || !!application.files?.[`pay${fileNum - 1}`];
                if (fileNum !== 1 && !hasPrevFile) return null;
                return (
                  <Upload
                    key={fileNum}
                    label={`${t('income_documents.pay')} #${fileNum}`}
                    field={`pay${fileNum}`}
                    required={!!requiredPayStubCount && ix < remainingPayRequired}
                    localFiles={localFiles}
                    setLocalFiles={setLocalFiles}
                    application={application}
                    requirePDF={requirePDF}
                  />
                );
              })}
            </div>
          </StyledFormGroup>
          <StyledFormGroup
            label={`${t('income_documents.bank')} ${
              requiredBankStatementCount ? `${bankUploadCount}/${requiredBankStatementCount}` : ''
            }`}
            subText={
              requiredBankStatementCount
                ? `${bankUploadCount} of ${requiredBankStatementCount} required files uploaded`
                : ''
            }>
            <div>
              {fileRefBankCount > 0 && (
                <TeamUploadNotice>
                  {fileRefBankCount} file{fileRefBankCount !== 1 ? 's' : ''} uploaded on your behalf
                  by our team
                </TeamUploadNotice>
              )}
              {Array.from({ length: MAX_FILE_COUNT }).map((unused, ix) => {
                const fileNum = ix + 1;
                const hasPrevFile =
                  !!localFiles?.[`bank${fileNum - 1}`] ||
                  !!application.files?.[`bank${fileNum - 1}`];
                if (fileNum !== 1 && !hasPrevFile) return null;
                return (
                  <Upload
                    key={fileNum}
                    label={`${t('income_documents.bank')} #${fileNum}`}
                    field={`bank${fileNum}`}
                    required={!!requiredBankStatementCount && ix < remainingBankRequired}
                    localFiles={localFiles}
                    setLocalFiles={setLocalFiles}
                    application={application}
                    requirePDF={requirePDF}
                  />
                );
              })}
            </div>
          </StyledFormGroup>
          {requiredTaxDocCount > 0 && (
            <StyledFormGroup label={t('income_documents.tax')}>
              <div>
                {fileRefTaxCount > 0 && (
                  <TeamUploadNotice>
                    {fileRefTaxCount} file{fileRefTaxCount !== 1 ? 's' : ''} uploaded on your behalf
                    by our team
                  </TeamUploadNotice>
                )}
                {Array.from({ length: MAX_FILE_COUNT }).map((unused, ix) => {
                  const fileNum = ix + 1;
                  const hasPrevFile =
                    !!localFiles?.[`tax${fileNum - 1}`] ||
                    !!application.files?.[`tax${fileNum - 1}`];
                  if (fileNum !== 1 && !hasPrevFile) return null;
                  return (
                    <Upload
                      key={fileNum}
                      label={`${t('income_documents.tax')} #${fileNum}`}
                      field={`tax${fileNum}`}
                      required={!!requiredTaxDocCount && ix < remainingTaxRequired}
                      localFiles={localFiles}
                      setLocalFiles={setLocalFiles}
                      application={application}
                      requirePDF={requirePDF}
                    />
                  );
                })}
              </div>
            </StyledFormGroup>
          )}
        </Row>
        <Row className="center flex gap-10">
          {!hideBack && (
            <Button radius="25px" width={90} variant="secondary" onClick={onBack}>
              {t('buttonsActions.backButton')}
            </Button>
          )}
          <Button
            disabled={!requirementsMet}
            width={90}
            radius="25px"
            variant="primary"
            onClick={handleNext}>
            {t('buttonsActions.next')}
          </Button>
        </Row>
      </PageContainer>
    </PageWrapper>
  );
};

export default (props) => {
  const { t } = useTranslation();

  return (
    <PageWrapper>
      <PageContainer>
        <Row className="center text-center">
          <Title>{t('income_documents.title')}</Title>
        </Row>
        <Row>
          <Description className="text-center">
            {props?.location?.companyInfo?.fileUploadMessage ? (
              <ShadowRender html={props?.location?.companyInfo?.fileUploadMessage} />
            ) : (
              t('income_documents.description')
            )}
          </Description>
        </Row>
      </PageContainer>
      <IncomeFiles {...props} />
    </PageWrapper>
  );
};
